import AJXLoader from "@components/AJXLoader";
import LoadError from "@components/LoadError";
import { apiSend, getRoute } from "@services/axios";
import { useTranslation } from "react-i18next";
import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { LucidePaperclip, LucideX, LucideFile, LucideImage, LucideCheck, LucideClock, LucideAlertCircle, LucideEllipsisVertical } from "lucide-react";
import { HiBookmark, HiOutlineAtSymbol, HiOutlineFaceSmile, HiOutlinePaperClip, HiPaperAirplane } from "react-icons/hi2";
import folderIcon from "../../../../assets/folder.png"
import { Button, Offcanvas } from "react-bootstrap";


const Details = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const [show, setShow] = useState(false);

    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [ticket, setTicket] = useState(null);
    const [newMessage, setNewMessage] = useState('');
    const [attachments, setAttachments] = useState([]);
    const [isSending, setIsSending] = useState(false);

    // Référence pour le champ de fichier
    const fileInputRef = useRef(null);
    const messagesEndRef = useRef(null);

    // Scroll to bottom when messages change
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [ticket?.messages]);

    useEffect(() => {
        const fetchTicket = async () => {
            try {
                setIsLoading(true);
                setError(null);

                const response = await apiSend(
                    [getRoute("TICKET_RETRIEVE"), id].join("/"),
                    "get"
                );

                if (response?.data?.request?.status !== true) {
                    throw new Error("Ticket not found");
                }

                setTicket(response.data.result);
            } catch (err) {
                setError(err.message || t("not_found"));
            } finally {
                setIsLoading(false);
            }
        };

        if (id) fetchTicket();
    }, [id, t]);

    if (isLoading) return <AJXLoader />;
    if (error) return <LoadError message={error} />;
    if (!ticket) return <LoadError message={t("not_found")} />;

    // Format date helper with error handling for invalid dates
    const formatDate = (dateString) => {
        if (!dateString) return "N/A";

        try {
            const fixedDateString = dateString.replace(
                /(\d{4}-\d{2}-\d{2})\s+(\d{2}):(\d{2})/,
                (match, date, hour, minute) => {
                    const validMinute = parseInt(minute) >= 60 ? '59' : minute;
                    return `${date} ${hour}:${validMinute}`;
                }
            );

            const date = new Date(fixedDateString);
            if (isNaN(date.getTime())) return dateString;

            return date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (e) {
            return dateString;
        }
    };

    // Get initials from name
    const getInitials = (name) => {
        if (!name) return "?";
        return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
    };

    // Get sender display name
    const getSenderName = (sender) => {
        if (!sender) return "System";
        return sender.name || sender.type || "Unknown";
    };

    // Get status badge class
    const getStatusBadgeClass = (status) => {
        switch (status) {
            case "new":
                return "badge-confirmed";
            case "in_progress":
                return "badge-completed";
            case "waiting":
                return "badge-pending";
            default:
                return "badge-closed";
        }
    };

    // Get priority badge class
    const getPriorityBadgeClass = (priority) => {
        switch (priority?.toLowerCase()) {
            case 'high':
                return 'badge-pending';
            case 'medium':
                return 'badge-confirmed';
            case 'low':
                return 'badge-completed';
            default:
                return 'badge-cancelled';
        }
    };

    // Gestion des pièces jointes
    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);
        const newAttachments = files.map(file => ({
            file,
            id: Math.random().toString(36).substr(2, 9),
            name: file.name,
            size: file.size,
            type: file.type,
            preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null
        }));

        setAttachments(prev => [...prev, ...newAttachments]);

        // Réinitialiser l'input file pour permettre de sélectionner le même fichier
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const removeAttachment = (id) => {
        setAttachments(prev => {
            const attachment = prev.find(a => a.id === id);
            if (attachment?.preview) {
                URL.revokeObjectURL(attachment.preview);
            }
            return prev.filter(a => a.id !== id);
        });
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const getFileIcon = (fileType) => {
        if (fileType?.startsWith('image/')) return <LucideImage size={16} />;
        return <LucideFile size={16} />;
    };
    const handleSendMessage = async () => {
        if (!newMessage.trim() && attachments.length === 0) return;

        const tempId = Date.now();
        const messageToSend = newMessage; // ✅ FIX

        const tempMessage = {
            id: tempId,
            message: messageToSend,
            created_at: new Date().toISOString(),
            sender: {
                id: "current-user-id",
                name: "Vous",
                type: "user"
            },
            attachments: attachments.map(att => ({
                id: att.id,
                name: att.name,
                url: att.preview,
                size: att.size,
                type: att.type
            })),
            status: 'sending'
        };

        try {
            setIsSending(true);
            setError(null);

            // ✅ Optimistic update
            setTicket(prev => ({
                ...prev,
                messages: [...(prev.messages || []), tempMessage]
            }));

            // ✅ Reset UI
            setNewMessage('');
            const currentAttachments = [...attachments];
            setAttachments([]);

            currentAttachments.forEach(att => {
                if (att.preview) URL.revokeObjectURL(att.preview);
            });

            const formData = new FormData();
            formData.append('message', messageToSend); // ✅ FIX


            const response = await apiSend(
                [getRoute("NEW_MESSAGE"), id, "messages"].join("/"),
                "post",
                formData,

            );

            if (response?.data?.request?.status === true) {
                const createdMessage = response.data.result;
                const messageId = createdMessage?.message_id;

                // ✅ Upload attachments AFTER message is created
                if (currentAttachments.length > 0 && messageId) {
                    const formDataUpload = new FormData();

                    currentAttachments.forEach(({ file }) => {
                        if (file) formDataUpload.append("attachment[]", file);
                    });

                    await apiSend(
                        [getRoute("ticket_attachement_upload"), id, messageId].join("/"),
                        "upload",
                        formDataUpload
                    );
                }

                // ✅ Update message status
                setTicket(prev => ({
                    ...prev,
                    messages: prev.messages.map(msg =>
                        msg.id === tempId
                            ? {
                                ...msg,
                                status: 'sent',
                                ...createdMessage
                            }
                            : msg
                    )
                }));

                setTimeout(refreshTicket, 1000);
            }
            else {
                throw new Error("failed");
            }

        } catch (error) {
            console.error('Error sending message:', error);

            setTicket(prev => ({
                ...prev,
                messages: prev.messages.map(msg =>
                    msg.id === tempId
                        ? {
                            ...msg,
                            status: 'failed',
                            errorMessage: t("message_failed") // ✅ i18n
                        }
                        : msg
                )
            }));
        } finally {
            setIsSending(false);
        }
    };

    // Fonction pour rafraîchir le ticket
    const refreshTicket = async () => {
        try {
            const response = await apiSend(
                [getRoute("TICKET_RETRIEVE"), id].join("/"),
                "get"
            );

            if (response?.data?.request?.status === true) {
                setTicket(response.data.result);
            }
        } catch (err) {
            console.error('Error refreshing ticket:', err);
        }
    };

    // Fonction pour réessayer l'envoi d'un message échoué
    const retrySendMessage = (failedMessage) => {
        // Récupérer le contenu du message
        const messageContent = failedMessage.message || failedMessage.content;

        // Réinitialiser le champ de message
        setNewMessage(messageContent);

        // Supprimer le message échoué de la liste
        setTicket(prev => ({
            ...prev,
            messages: prev.messages.filter(msg => msg.id !== failedMessage.id)
        }));
    };



    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    // Rendu du message avec son statut
    const renderMessage = (msg) => {
        const isCurrentUser = msg.sender?.type === "provider";;

        return (
            <div key={msg.id} className={`d-flex ${isCurrentUser ? "justify-content-start" : "justify-content-end"} pb-3`}>
                <div className="d-flex gap-3">
                    <div className="d-none d-md-block">
                        <span className="avatar rounded-circle bg-gradient-orange text-white d-flex align-items-center justify-content-center"
                            style={{ width: '40px', height: '40px', fontSize: '14px', fontWeight: 600 }}>
                            {getInitials(getSenderName(msg.sender))}
                        </span>
                    </div>
                    <div className="">
                        <div className="d-flex flex-column flex-md-row  align-items-center mb-2">
                            <span className="fw-semibold d-none d-md-block">
                                {getSenderName(msg.sender)}
                                {msg.status === 'sending' && (
                                    <small className="ms-2 text-secondary">
                                        <LucideClock size={14} className="me-1" />
                                        {t("message_sending")}
                                    </small>
                                )}
                                {msg.status === 'sent' && (
                                    <small className="ms-2 text-success">
                                        <LucideCheck size={14} className="me-1" />

                                    </small>
                                )}
                                {msg.status === 'failed' && (
                                    <small className="ms-2 text-danger">
                                        <LucideAlertCircle size={14} className="me-1" />
                                        {msg.errorMessage || t("message_failed")}
                                    </small>
                                )}
                            </span>
                            <small className="text-secondary ms-2 ">
                                <i className="bi bi-clock me-1"></i>
                                {formatDate(msg.created_at)}
                            </small>
                        </div>

                        <p className="mb-2 text-dark text-wrap text-break rounded-bottom-4 rounded-end-4 bg-blue text-white p-3">
                            {msg.message || msg.content}
                        </p>

                        {/* Afficher les pièces jointes du message */}
                        {(msg.attachments?.length > 0 || msg.files?.length > 0) && (
                            <div className="mt-2 d-flex gap-2 flex-wrap">
                                {(msg.attachments || msg.files || []).map((att, idx) => (
                                    <a
                                        key={idx}
                                        href={att.url || att.path}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="badge bg-light text-decoration-none p-2"
                                    >
                                        <LucidePaperclip size={14} className="me-1" />
                                        {att.name || att.filename || t('attachment')}
                                    </a>
                                ))}
                            </div>
                        )}



                        {/* Bouton réessayer pour les messages échoués */}
                        {msg.status === 'failed' && (
                            <button
                                className="btn btn-link btn-sm text-danger p-0 mt-2"
                                onClick={() => retrySendMessage(msg)}
                            >
                                <i className="bi bi-arrow-counterclockwise me-1"></i>
                                {t("retry")}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <>



            <div className="pe-3 row " style={{ height: "calc(100vh - 120px)", overflow: "hidden" }}>
                <Offcanvas show={show} onHide={handleClose} >
                    <Offcanvas.Header closeButton>
          <Offcanvas.Title>Offcanvas</Offcanvas.Title>
        </Offcanvas.Header>
                    <Offcanvas.Body>
                      <h4 className="mb-0 fw-bold  text-black">
                                {t("ticket")} #{ticket.id} - {ticket.subject}
                            </h4>
                             <div className="d-flex align-items-center gap-1 gap-md-2 flex-wrap">
                            <span className={`badge ${getStatusBadgeClass(ticket.status)} px-2 px-md-3 py-1 py-md-2 text-uppercase small `}>
                                {ticket.status || 'unknown'}
                            </span>

                            <span className={`badge ${getPriorityBadgeClass(ticket.priority)} px-2 px-md-3 py-1 py-md-2 text-uppercase small `}>
                                {ticket.priority || 'low'} {t("priority")}
                            </span>
                           
                        </div>
                           <div className="col-lg-3 bg-secondary-blue h-100 rounded-1 ">
                    <div className=" pt-4 pb-3 px-4">
                        <h4 className="mb-0 pt-2 fw-semibold">Ticket Metadata</h4>
                    </div>
                    <div className=" px-4 pb-4 pt-2">
                        <div className="row mb-3">
                            <div className="col-12 col-sm-4 col-lg-12 col-xl-4">
                                <span className="text-secondary text-muted small">{t("submisssion")}</span>
                            </div>
                            <div className="col-sm-8 col-lg-12 col-xl-8">
                                <span className="fw-semibold">{formatDate(ticket.created_at)}</span>
                            </div>
                        </div>
                        <div className="row mb-3">
                            <div className="col-12 col-sm-4 col-lg-12 col-xl-4">
                                <span className="text-secondary text-muted small">{t("requester")}</span>
                            </div>
                            <div className="col-sm-8 col-lg-12 col-xl-8">
                                <span className="fw-semibold">{ticket.createBy || 'undefined'}</span>
                            </div>
                        </div>
                        <div className="row mb-3">
                            <div className="col-12 col-sm-4 col-lg-12 col-xl-4">
                                <span className="text-secondary text-muted small">{t("Assignee")}</span>
                            </div>
                            <div className="col-sm-8 col-lg-12 col-xl-8">
                                <span className="fw-semibold">{ticket.service}</span>
                            </div>
                        </div>

                    </div>
                    <h4 className="px-4 mb-0 fw-semibold text-capitalize">

                        {t("files_shared")}
                    </h4>

                    <div className="d-flex flex-column ms-4 justify-content-center align-items-center text-center py-4">

                        <img alt="folderIcon" src={folderIcon} width={200} />
                        <span className="text-muted small text-start">{t("no_files_yet")}</span>
                    </div>
                </div>

                    </Offcanvas.Body>
                </Offcanvas>
                <div className="col-lg-9 d-flex flex-column h-100 p-0">

                    <div className="d-flex  flex-md-row py-2 align-items-start align-items-md-center justify-content-between gap-2 px-2 px-md-4 bg-white mx-2 mx-md-3 rounded-1"
                        style={{ boxShadow: "0 4px 6px -2px rgba(0,0,0,0.1)" }}>

                        <div className="d-inline-flex py-2 py-md-3 align-items-center gap-2">
                            <HiBookmark size={18} className="text-blue d-none d-lg-block" />

                            <h6 className="mb-0 fw-bold d-md-none">
                                {t("ticket")} #{ticket.id}
                            </h6>

                            <h4 className="mb-0 fw-bold d-none d-md-block">
                                {t("ticket")} #{ticket.id} - {ticket.subject}
                            </h4>
                        </div>

                        <div className="d-flex align-items-center gap-1 gap-md-2 flex-wrap">
                            <span className={`badge ${getStatusBadgeClass(ticket.status)} px-2 px-md-3 py-1 py-md-2 text-uppercase small d-none d-md-block`}>
                                {ticket.status || 'unknown'}
                            </span>

                            <span className={`badge ${getPriorityBadgeClass(ticket.priority)} px-2 px-md-3 py-1 py-md-2 text-uppercase small d-none d-md-block`}>
                                {ticket.priority || 'low'} {t("priority")}
                            </span>
                            <Button className=" px-2 px-md-3 py-1 py-md-2 text-uppercase small d-lg-none" onClick={handleShow}>
                                <LucideEllipsisVertical />
                            </Button>
                        </div>

                    </div>


                    {/* Chat container */}
                    <div className="d-flex flex-column flex-grow-1 overflow-hidden ">
                        {/* Messages section avec hauteur maximale et défilement */}
                        <div className="flex-grow-1 overflow-auto px-4 pt-4">
                            {ticket.messages?.length > 0 ? (
                                <div className="list-group list-group-flush">
                                    {ticket.messages.map((msg) => renderMessage(msg))}
                                    <div ref={messagesEndRef} />
                                </div>
                            ) : (
                                <div className="text-center py-5">
                                    <i className="bi bi-chat-dots text-secondary" style={{ fontSize: '3rem' }}></i>
                                    <p className="text-secondary mt-3 mb-0">{t("no_messages_yet")}</p>
                                </div>
                            )}
                        </div>

                        {/* Section pour écrire un message avec pièces jointes */}

                        <div
                            className=" px-3 pt-3"
                            style={{
                                position: "sticky",
                                bottom: 0,
                                zIndex: 10
                            }}
                        >
                            <div className="bg-white rounded-3">
                                <div className="d-flex flex-column flex-md-row p-2">

                                    <textarea
                                        className="form-control border-0 rounded-3 p-2 msg-focus"
                                        id="messageInput"
                                        rows="1"
                                        placeholder={t("type_your_message") || "Type your message here..."}
                                        value={newMessage}
                                        onChange={(e) => setNewMessage(e.target.value)}
                                        onKeyDown={handleKeyDown}
                                        disabled={isSending}
                                        style={{
                                            backgroundColor: 'white',
                                            resize: 'none',
                                            fontSize: '0.95rem'
                                        }}
                                    />

                                    <div className="d-flex justify-content-end align-items-center gap-2">


                                        <HiOutlinePaperClip size={24} className="text-blue cursor-pointer"
                                            onClick={() => fileInputRef.current?.click()}
                                            disabled={isSending} />



                                        <input
                                            type="file"
                                            ref={fileInputRef}
                                            onChange={handleFileSelect}
                                            multiple
                                            style={{ display: 'none' }}
                                            disabled={isSending}
                                        />

                                        <button
                                            className="rounded btn-custom p-2"
                                            onClick={handleSendMessage}
                                            disabled={isSending || (!newMessage.trim() && attachments.length === 0)}
                                            type="button"

                                        >
                                            {isSending ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                                    {t("sending") || "Sending..."}
                                                </>
                                            ) : (
                                                <>
                                                    <span className="d-none d-md-block">{t("send") || "Send"}</span>
                                                    <HiPaperAirplane size={17} className="ms" />
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>

                                {/* Aperçu des pièces jointes */}
                                {attachments.length > 0 && (
                                    <div className="mb-3">
                                        <div className="d-flex flex-wrap gap-2">
                                            {attachments.map((att) => (
                                                <div
                                                    key={att.id}
                                                    className="bg-white rounded p-2 d-flex align-items-center gap-2"
                                                    style={{ maxWidth: '200px' }}
                                                >
                                                    {att.preview ? (
                                                        <img
                                                            src={att.preview}
                                                            alt={att.name}
                                                            style={{ width: '30px', height: '30px', objectFit: 'cover' }}
                                                            className="rounded"
                                                        />
                                                    ) : (
                                                        <div className="bg-light rounded p-1">
                                                            {getFileIcon(att.type)}
                                                        </div>
                                                    )}
                                                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                                        <div className="small fw-semibold text-truncate">
                                                            {att.name}
                                                        </div>
                                                        <div className="small text-secondary">
                                                            {formatFileSize(att.size)}
                                                        </div>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className="btn btn-link p-0 text-danger"
                                                        onClick={() => removeAttachment(att.id)}
                                                        disabled={isSending}
                                                    >
                                                        <LucideX size={16} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}






                            </div>
                        </div>

                    </div>

                </div>

                <div className="col-lg-3 bg-secondary-blue h-100 rounded-1 d-none d-md-block">
                    <div className=" pt-4 pb-3 px-4">
                        <h4 className="mb-0 pt-2 fw-semibold">Ticket Metadata</h4>
                    </div>
                    <div className=" px-4 pb-4 pt-2">
                        <div className="row mb-3">
                            <div className="col-12 col-sm-4 col-lg-12 col-xl-4">
                                <span className="text-secondary text-muted small">{t("submisssion")}</span>
                            </div>
                            <div className="col-sm-8 col-lg-12 col-xl-8">
                                <span className="fw-semibold">{formatDate(ticket.created_at)}</span>
                            </div>
                        </div>
                        <div className="row mb-3">
                            <div className="col-12 col-sm-4 col-lg-12 col-xl-4">
                                <span className="text-secondary text-muted small">{t("requester")}</span>
                            </div>
                            <div className="col-sm-8 col-lg-12 col-xl-8">
                                <span className="fw-semibold">{ticket.createBy || 'undefined'}</span>
                            </div>
                        </div>
                        <div className="row mb-3">
                            <div className="col-12 col-sm-4 col-lg-12 col-xl-4">
                                <span className="text-secondary text-muted small">{t("Assignee")}</span>
                            </div>
                            <div className="col-sm-8 col-lg-12 col-xl-8">
                                <span className="fw-semibold">{ticket.service}</span>
                            </div>
                        </div>

                    </div>
                    <h4 className="px-4 mb-0 fw-semibold text-capitalize">

                        {t("files_shared")}
                    </h4>

                    <div className="d-flex flex-column ms-4 justify-content-center align-items-center text-center py-4">

                        <img alt="folderIcon" src={folderIcon} width={200} />
                        <span className="text-muted small text-start">{t("no_files_yet")}</span>
                    </div>
                </div>
            </div>

        </>
    );
};

export default Details;